import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {createRefetchContainer, graphql} from 'react-relay';
import {Redirect, Route, Switch, useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {SubNavigation} from 'web-components';
import {PageWrapper, StandardMargin} from './InvoicingPage.styled';
import {MODULE_TYPES} from '../../../constants';
import Util from '../../../forecast-app/shared/util/util.js';
import {hasModule} from '../../../forecast-app/shared/util/module_util';
import ForecastQueryRenderer from '../../../ForecastQueryRenderer';
import OverviewPage, {getDefaultTimeAndMaterialEndDate, getDefaultTimeAndMaterialStartDate, overviewPageQuery} from './OverviewPage';
import InvoicesPage, {invoicesPageQuery} from './InvoicesPage';
import StandaloneEditInvoiceModalWrapper from '../../../containers/modal/edit_invoice_modal/StandaloneEditInvoiceModalWrapper';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';

export const InvoicingPageV2 = ({viewer}) => {
	const INVOICING_PAGES = {
		OVERVIEW: 'overview',
		INVOICES: 'invoices',
	};

	const localStorageKey = 'invoicing_selected_tab_v2';
	const {path} = useRouteMatch();
	const history = useHistory();
	const {pathname} = useLocation();
	const intl = useIntl();
	const [invoiceMonth, setInvoiceMonth] = useState(null);
	useEffect(() => {
		// Module check
		if (!hasModule(MODULE_TYPES.FINANCE, viewer.company.modules)) {
			history.push('/');
			Util.localStorageSetItem('portfolio-section-last-viewed', '');
		}

		// authorization cehck
		if (!hasPermission(PERMISSION_TYPE.INVOICE_READ)) {
			// if user doesnt have access rights to view this page redirect to no access page
			history.push('/not-authorized');
			Util.localStorageSetItem('portfolio-section-last-viewed', '');
		}
	}, []);

	const INVOICING_PAGES_NAVIGATION_OPTONS = [
		{
			value: INVOICING_PAGES.OVERVIEW,
			label: intl.formatMessage({id: 'project_overview.title'}),
		},
		{
			value: INVOICING_PAGES.INVOICES,
			label: intl.formatMessage({id: 'common.invoices'}),
		},
	];

	const getActivePage = () => {
		let activePage = '';
		INVOICING_PAGES_NAVIGATION_OPTONS.forEach(option => {
			if (pathname.includes(option.value)) {
				activePage = option.value;
			}
		});
		return activePage;
	};

	const handleSetActivePage = value => {
		if (getActivePage() !== value && INVOICING_PAGES_NAVIGATION_OPTONS.some(option => option.value === value)) {
			history.push(value);
			Util.localStorageSetItem(localStorageKey, value);
		}
	};

	const handleChangePage = (page, month) => {
		setInvoiceMonth(month);
		handleSetActivePage(INVOICING_PAGES[page]);
	};

	const getHeaderTitle = () => {
		const content = [];
		const onboardingFlows =
			getActivePage() === INVOICING_PAGES.OVERVIEW
				? [
						{
							id: 'invoicing-intro',
							title: 'Invoicing - Intro',
							description: null,
							contentId: '1598357944mRey4692',
						},
						{id: 'invoicing-overview', title: 'Invoicing - Overview', description: null, contentId: '1598355662cHmx4887'},
						{id: 'invoicing-create', title: 'Invoicing - Create Invoice', description: null, contentId: '1598364244hPbw1568'},
				  ]
				: [
						{
							id: 'invoicing-invoices',
							title: 'Invoicing - Invoices',
							description: null,
							contentId: '1598359304gIyy1381',
						},
						{id: 'invoicing-invoices', title: 'Invoicing - View and Edit Invoice', description: null, contentId: '1598365308nDsz1242'},
				  ];

		const onboardingComponent = {
			id: 'onboarding-component',
			intl: intl,
			type: TopHeaderBar.TYPE.ONBOARDING,
			title: 'Invoicing - Overview',
			options: onboardingFlows,
			helpCenterLink: 'https://help.forecast.it/en/invoicing',
		};
		content.push(onboardingComponent);

		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar title={intl.formatMessage({id: 'common.invoicing'})} content={content} />
			</TopHeaderBarWrapper>
		);
	};

	return (
		<PageWrapper data-cy={'invoicing-page'}>
			{getHeaderTitle()}
			<div style={{marginTop: '20px'}}>
				<StandardMargin data-userpilot={'invoicing-tabs'}>
					{INVOICING_PAGES_NAVIGATION_OPTONS.map(option => (
						<SubNavigation.NavigationItem
							active={getActivePage() === option.value}
							onClick={() => handleSetActivePage(option.value)}
							key={option.value}
							cy={'invoicing-tabs-' + option.value.toLowerCase()}
							userpilot={'invoicing-tabs-' + option.value.toLowerCase()}
						>
							{option.label}
						</SubNavigation.NavigationItem>
					))}
				</StandardMargin>
			</div>
			<Switch>
				<Route path={`${path}`} exact>
					<Redirect to={`${path}/${Util.localStorageGetItem(localStorageKey) ? Util.localStorageGetItem(localStorageKey) : INVOICING_PAGES.OVERVIEW}`} />
				</Route>
				<Route exact path={`${path}/I-:companyInvoiceId`} render={routeProps => <Redirect to={`${path}/${INVOICING_PAGES.INVOICES}/I-${routeProps.match.params.companyInvoiceId}`} />} />

				<Route
					path={`${path}/${INVOICING_PAGES.OVERVIEW}`}
					render={() => (
						<ForecastQueryRenderer
							query={overviewPageQuery}
							key={'invoicing-overview'}
							componentName={'invoicing_overview_page'}
							variables={{
								startYear: getDefaultTimeAndMaterialStartDate().year(),
								startMonth: getDefaultTimeAndMaterialStartDate().month() + 1,
								startDay: getDefaultTimeAndMaterialStartDate().date(),
								endYear: getDefaultTimeAndMaterialEndDate().year(),
								endMonth: getDefaultTimeAndMaterialEndDate().month() + 1,
								endDay: getDefaultTimeAndMaterialEndDate().date(),
							}}
							showLoader
							render={relayProps => <OverviewPage onChangePage={handleChangePage} {...relayProps} />}
						/>
					)}
				/>
				<Route
					path={`${path}/${INVOICING_PAGES.INVOICES}`}
					render={() => (
						<ForecastQueryRenderer
							query={invoicesPageQuery}
							key={'invoicing-list-page'}
							componentName={'invoicing_list_page'}
							showLoader
							render={(relayProps, retry) => (
								<>
									<InvoicesPage onChangePage={handleChangePage} invoiceMonth={invoiceMonth} {...relayProps} />
									<Route
										path={`${path}/${INVOICING_PAGES.INVOICES}/I-:companyInvoiceId`}
										render={compObj => <StandaloneEditInvoiceModalWrapper retry={retry} {...compObj} companyInvoiceId={compObj.match.params.companyInvoiceId} />}
									/>
								</>
							)}
						/>
					)}
				/>
			</Switch>
		</PageWrapper>
	);
};

const invoicingPageV2Query = graphql`
	query InvoicingPageV2Query {
		viewer {
			actualPersonId
			component(name: "invoicing_page_v2")
			...InvoicingPageV2_viewer
		}
	}
`;

export {invoicingPageV2Query};

export default createRefetchContainer(InvoicingPageV2, {
	viewer: graphql`
		fragment InvoicingPageV2_viewer on Viewer {
			company {
				modules {
					moduleType
				}
			}
		}
	`,
});
